import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiGet } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

interface LedgerEntry {
  id: string;
  points: number;
  balanceAfter: number;
  referenceType: string;
  description: string | null;
  occurredAt: string;
}

interface LoyaltyAccount {
  id: string;
  programId: string;
  pointsBalance: number;
  lifetimePoints: number;
  tierCode: string | null;
  enrolledAt: string | null;
  rewards: unknown[];
  ledger: LedgerEntry[];
}

interface LoyaltyFullData {
  enrolled: boolean;
  accounts: LoyaltyAccount[];
  coupons: unknown[];
  redemptions: unknown[];
}

interface LoyaltyPointsProps {
  layout?: 'compact' | 'detailed';
  showHistory?: boolean;
  historyLimit?: number;
  title?: string;
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#11088;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view your points
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to check your balance and transaction history.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

const REF_TYPE_ICONS: Record<string, string> = {
  earn: '\u2B50',
  redeem: '\uD83C\uDF81',
  bonus: '\uD83C\uDF89',
  adjustment: '\u2699\uFE0F',
  contest: '\uD83C\uDFC6',
  referral: '\uD83D\uDC65',
  checkin: '\uD83D\uDCC5',
  mission: '\uD83C\uDFAF',
  purchase: '\uD83D\uDED2',
};

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export function LoyaltyPointsBlock({ props }: { props: LoyaltyPointsProps }) {
  const { isAuthenticated } = useAuth();
  const { data, loading: loyaltyLoading } = useLoyalty();
  const layout = props.layout ?? 'detailed';
  const showHistory = props.showHistory ?? true;
  const historyLimit = props.historyLimit ?? 10;
  const title = props.title ?? 'Points';

  const [account, setAccount] = useState<LoyaltyAccount | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    apiGet<LoyaltyFullData>('/customer/loyalty')
      .then((res) => {
        if (!cancelled && res.accounts?.length > 0) {
          setAccount(res.accounts[0]);
        }
      })
      .catch(() => { /* silently fail */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (loyaltyLoading || loading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-6 w-32 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="h-16 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-[var(--color-surface-border)] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;
  if (!account) {
    return (
      <div className="loyalty-card p-6 text-center text-[var(--color-text-muted)] text-sm">
        No loyalty account found.
      </div>
    );
  }

  const ledger = (account.ledger ?? []).slice(0, historyLimit);

  return (
    <div className="space-y-4">
      {/* Points summary */}
      <div className="loyalty-card p-6">
        <h2 className="loyalty-heading text-lg text-[var(--color-text)] mb-4">{title}</h2>
        <div className={`grid ${layout === 'detailed' ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2'} gap-4`}>
          <div className="text-center">
            <div className="text-2xl font-bold text-[var(--color-primary)]">
              {account.pointsBalance.toLocaleString()}
            </div>
            <div className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mt-1">
              Available
            </div>
          </div>
          {layout === 'detailed' && (
            <>
              <div className="text-center">
                <div className="text-2xl font-bold text-[var(--color-text)]">
                  {account.lifetimePoints.toLocaleString()}
                </div>
                <div className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mt-1">
                  Lifetime
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[var(--color-text)]">
                  {account.tierCode?.toUpperCase() ?? 'BASE'}
                </div>
                <div className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mt-1">
                  Current Tier
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[var(--color-text)]">
                  {account.enrolledAt
                    ? new Date(account.enrolledAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
                    : '-'}
                </div>
                <div className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mt-1">
                  Member Since
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Transaction history */}
      {showHistory && ledger.length > 0 && (
        <div className="loyalty-card overflow-hidden">
          <div className="p-4 border-b border-[var(--color-surface-border)]">
            <h3 className="loyalty-heading text-sm text-[var(--color-text)]">
              Transaction History
            </h3>
          </div>
          <div className="divide-y divide-[var(--color-surface-border)]">
            {ledger.map((entry) => {
              const icon = REF_TYPE_ICONS[entry.referenceType] ?? '\u2022';
              const isPositive = entry.points > 0;
              return (
                <div key={entry.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-surface)] transition-colors">
                  <span className="text-lg w-8 text-center flex-shrink-0">{icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--color-text)] truncate">
                      {entry.description ?? entry.referenceType.replace(/_/g, ' ')}
                    </div>
                    <div className="text-[10px] text-[var(--color-text-muted)]">
                      {formatDate(entry.occurredAt)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className={`text-sm font-bold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                      {isPositive ? '+' : ''}{entry.points.toLocaleString()} pts
                    </span>
                    <span className="text-[10px] text-[var(--color-text-muted)]">
                      bal: {entry.balanceAfter.toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showHistory && ledger.length === 0 && (
        <div className="loyalty-card p-6 text-center text-[var(--color-text-muted)] text-sm">
          No transactions yet. Start earning points!
        </div>
      )}
    </div>
  );
}
